import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function CompareTab({ hitters, pitchers }) {
  const [playerType, setPlayerType] = useState("hitters");
  const [player1Id, setPlayer1Id] = useState("");
  const [player2Id, setPlayer2Id] = useState("");

  const players = playerType === "hitters" ? hitters : pitchers;

  const player1 = useMemo(() => {
    return players.find(p => String(p.id) === player1Id) || null;
  }, [players, player1Id]);

  const player2 = useMemo(() => {
    return players.find(p => String(p.id) === player2Id) || null;
  }, [players, player2Id]);

  const hitterCompareStats = [
    { key: "games", label: "Games" },
    { key: "at_bats", label: "At Bats" },
    { key: "hits", label: "Hits" },
    { key: "doubles", label: "Doubles" },
    { key: "home_runs", label: "Home Runs" },
    { key: "rbi", label: "RBI" },
    { key: "runs", label: "Runs" },
    { key: "stolen_bases", label: "Stolen Bases" },
    { key: "walks", label: "Walks" },
    { key: "strikeouts", label: "Strikeouts" },
    { key: "avg", label: "AVG" },
    { key: "obp", label: "OBP" },
    { key: "slg", label: "SLG" },
    { key: "ops", label: "OPS" },
    { key: "babip", label: "BABIP" },
    { key: "fwar", label: "fWAR" },
    { key: "wrc_plus", label: "wRC+" }
  ];

  const pitcherCompareStats = [
    { key: "games", label: "Games" },
    { key: "games_started", label: "Games Started" },
    { key: "wins", label: "Wins" },
    { key: "losses", label: "Losses" },
    { key: "saves", label: "Saves" },
    { key: "holds", label: "Holds" },
    { key: "innings_pitched", label: "Innings Pitched" },
    { key: "hits", label: "Hits Allowed" },
    { key: "runs", label: "Runs" },
    { key: "earned_runs", label: "Earned Runs" },
    { key: "home_runs", label: "HR Allowed" },
    { key: "walks", label: "Walks" },
    { key: "strikeouts", label: "Strikeouts" },
    { key: "era", label: "ERA" },
    { key: "whip", label: "WHIP" },
    { key: "fip", label: "FIP" },
    { key: "k_per_9", label: "K/9" },
    { key: "bb_per_9", label: "BB/9" },
    { key: "babip", label: "BABIP" },
    { key: "avg_against", label: "BAA" },
    { key: "fwar", label: "fWAR" }
  ];

  const compareStats = playerType === "hitters" ? hitterCompareStats : pitcherCompareStats;

  // Context-aware: different stats are "lower is better" for hitters vs pitchers
  const hitterLowerIsBetter = ["strikeouts"]; // Only K's: fewer = better for hitters
  const pitcherLowerIsBetter = ["losses", "runs", "earned_runs", "home_runs", "walks", "hits", "era", "whip", "fip", "bb_per_9", "babip", "avg_against"];

  const lowerIsBetter = playerType === "hitters" ? hitterLowerIsBetter : pitcherLowerIsBetter;

  const getComparisonClass = (statKey, val1, val2) => {
    if (!val1 || !val2) return "";
    const num1 = parseFloat(String(val1).replace(",", ""));
    const num2 = parseFloat(String(val2).replace(",", ""));
    if (isNaN(num1) || isNaN(num2) || num1 === num2) return "";

    const isBetter = lowerIsBetter.includes(statKey) ? num1 < num2 : num1 > num2;
    return isBetter ? "text-astros-success font-bold" : "";
  };

  const handleTypeChange = (value) => {
    setPlayerType(value);
    setPlayer1Id("");
    setPlayer2Id("");
  };

  return (
    <div data-testid="compare-tab">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-4">
        PLAYER COMPARISON
      </h2>
      
      <p className="text-astros-muted text-sm mb-6">
        Compare two players of the same type (hitters vs hitters, or pitchers vs pitchers)
      </p>

      {/* Type Selection */}
      <div className="mb-6">
        <label className="block text-xs text-astros-muted uppercase tracking-wider mb-2 font-bold">
          Player Type
        </label>
        <div className="flex gap-4">
          <button
            onClick={() => handleTypeChange("hitters")}
            className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
              playerType === "hitters"
                ? "text-astros-orange border-b-2 border-astros-orange"
                : "text-astros-muted hover:text-white"
            }`}
            data-testid="compare-type-hitters"
          >
            HITTERS
          </button>
          <button
            onClick={() => handleTypeChange("pitchers")}
            className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
              playerType === "pitchers"
                ? "text-astros-orange border-b-2 border-astros-orange"
                : "text-astros-muted hover:text-white"
            }`}
            data-testid="compare-type-pitchers"
          >
            PITCHERS
          </button>
        </div>
      </div>

      {/* Player Selection */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-xs text-astros-muted uppercase tracking-wider mb-2 font-bold">
            Player 1
          </label>
          <Select value={player1Id} onValueChange={setPlayer1Id}>
            <SelectTrigger 
              className="bg-astros-surface border-astros-border text-white"
              data-testid="select-player-1"
            >
              <SelectValue placeholder="Select a player" />
            </SelectTrigger>
            <SelectContent className="bg-astros-surface border-astros-border">
              {players.map((p) => (
                <SelectItem 
                  key={p.id} 
                  value={String(p.id)}
                  className="text-white hover:bg-astros-surface-hover focus:bg-astros-surface-hover"
                >
                  {p.name} ({p.position})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs text-astros-muted uppercase tracking-wider mb-2 font-bold">
            Player 2
          </label>
          <Select value={player2Id} onValueChange={setPlayer2Id}>
            <SelectTrigger 
              className="bg-astros-surface border-astros-border text-white"
              data-testid="select-player-2"
            >
              <SelectValue placeholder="Select a player" />
            </SelectTrigger>
            <SelectContent className="bg-astros-surface border-astros-border">
              {players.map((p) => (
                <SelectItem 
                  key={p.id} 
                  value={String(p.id)}
                  className="text-white hover:bg-astros-surface-hover focus:bg-astros-surface-hover"
                >
                  {p.name} ({p.position})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comparison Display */}
      {player1 && player2 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-astros-surface border border-astros-border"
        >
          {/* Header */}
          <div className="grid grid-cols-3 border-b border-astros-border">
            <div className="p-4 flex items-center gap-3">
              <img
                src={player1.photo_url}
                alt={player1.name}
                className="w-12 h-12 object-cover bg-astros-navy"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=80&h=80&fit=crop"; }}
              />
              <div>
                <p className="font-ibm-plex font-semibold text-white">{player1.name}</p>
                <p className="text-xs text-astros-muted">#{player1.jersey_number} {player1.position}</p>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center text-astros-muted font-bebas text-xl">
              VS
            </div>
            <div className="p-4 flex items-center gap-3 justify-end">
              <div className="text-right">
                <p className="font-ibm-plex font-semibold text-white">{player2.name}</p>
                <p className="text-xs text-astros-muted">#{player2.jersey_number} {player2.position}</p>
              </div>
              <img
                src={player2.photo_url}
                alt={player2.name}
                className="w-12 h-12 object-cover bg-astros-navy"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=80&h=80&fit=crop"; }}
              />
            </div>
          </div>

          {/* Stats Comparison */}
          <div className="divide-y divide-astros-border">
            {compareStats.map((stat) => (
              <div key={stat.key} className="grid grid-cols-3 py-3 px-4">
                <div className={`text-left ${getComparisonClass(stat.key, player1.stats?.[stat.key], player2.stats?.[stat.key])}`}>
                  {player1.stats?.[stat.key] ?? "-"}
                </div>
                <div className="text-center text-xs text-astros-muted uppercase tracking-wider">
                  {stat.label}
                </div>
                <div className={`text-right ${getComparisonClass(stat.key, player2.stats?.[stat.key], player1.stats?.[stat.key])}`}>
                  {player2.stats?.[stat.key] ?? "-"}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <div className="bg-astros-surface border border-astros-border p-12 text-center">
          <p className="text-astros-muted">
            Select two {playerType === "hitters" ? "hitters" : "pitchers"} to compare their stats
          </p>
        </div>
      )}
    </div>
  );
}
